import flatten from 'lodash/flatten';
import omit from 'lodash/omit';
import { SERVICE_POINT_OPERATOR, USER_TYPE, FIELD_NAMES, ERROR_MESSAGES } from '../../constants';
import { userDataFields, addressDetailsFields } from '../address/input-definitions';
import invoiceDefinitions from '../invoice/invoice-definitions';
import invoiceUtils from '../invoice/invoice-utils';
import {
  AddressState,
  FormInputEntry,
  FormInputPayload,
  OrderStore,
  FormField,
  InvoiceState,
} from './order-store';

// eslint-disable-next-line import/prefer-default-export
export function stripCountryCode(phoneNumber: string): string {
  return phoneNumber.replace(/^48|^\+48/, '');
}

type InputField = {
  input: string;
  value: string;
  valid: boolean;
};

const atlasContactDataFields = {
  EMAIL: FIELD_NAMES.email,
  PHONE_NUMBER: FIELD_NAMES.phoneNumber,
} as const;
const { EMAIL, PHONE_NUMBER } = atlasContactDataFields;

export function getInputFieldsValuesFromResponse(prepopulatedUserData: Record<string, string>) {
  return Object.keys(prepopulatedUserData).reduce((acc, key: string) => {
    if (prepopulatedUserData[key] && key !== EMAIL) {
      return {
        ...acc,
        [key]: {
          input: key,
          value:
            key === PHONE_NUMBER
              ? stripCountryCode(prepopulatedUserData[key])
              : prepopulatedUserData[key],
          valid: Boolean(prepopulatedUserData[key]),
        },
      };
    }
    return acc;
  }, {} as unknown as Record<string, InputField>);
}

export const fieldsValidation = (address: AddressState, operator: string): boolean => {
  const fields: FormField[] =
    operator === SERVICE_POINT_OPERATOR.poczta
      ? flatten([...userDataFields, ...addressDetailsFields])
      : [...userDataFields];

  return fields.reduce<boolean>((valid: boolean, { name, required }: FormField) => {
    if (!required) {
      return valid;
    }

    return !!(valid && address[name] && address[name].valid);
  }, true);
};

export const isInvoiceValid = (
  invoice: Record<string, FormInputEntry>,
  invoiceType: string,
): boolean => {
  const { privateUserFields, businessUserFields } = invoiceDefinitions;
  const fields = {
    [USER_TYPE.BUSINESS]: businessUserFields,
    [USER_TYPE.PRIVATE]: privateUserFields,
  };
  const formFields: FormField[] = flatten(fields[invoiceType]);

  return formFields.reduce((valid: boolean, { name, required }: FormField): boolean => {
    if (!required) {
      return valid;
    }
    return !!(valid && invoice[name] && invoice[name].valid);
  }, true);
};

export const updateStateOnAddressError = (
  state: OrderStore,
  field: string,
  errorMessage = 'dlv_validation_required',
  clearField?: boolean,
) => ({
  ...state,
  valid: false,
  loading: false,
  confirming: false,
  orderErrors: [...(state.orderErrors || []), [`dlv_addressinfo_${field}`, errorMessage]],
  address: {
    ...state.address,
    [field]: {
      ...state.address[field],
      value: clearField ? '' : state.address[field]?.value,
      valid: false,
      error: errorMessage,
    },
  },
});

export const updateStateOnInvoiceError = (
  state: OrderStore,
  field: string,
  errorMessage = 'dlv_validation_required',
  clearField?: boolean,
) => ({
  ...state,
  valid: false,
  loading: false,
  confirming: false,
  orderErrors: [...(state.orderErrors || []), [`dlv_addressinfo_${field}`, errorMessage]],
  invoice: {
    ...state.invoice,
    [field]: {
      ...state.invoice[field],
      value: clearField ? '' : state.invoice[field]?.value,
      valid: false,
      error: errorMessage,
    },
  },
});

export const updateFieldOnSetValue = (fieldData: FormInputEntry, payload: FormInputPayload) => {
  const updatedField = { ...fieldData, ...payload };
  if (payload.valid && !!fieldData?.error) {
    delete updatedField.error;
  }
  return updatedField;
};

export const removeWhiteSpaceSanitizer = (string: string) => string.replace(/\s/g, '');

export const getConfirmationClearedFields = (
  isChecked: boolean,
  invoiceType: string,
  invoice: InvoiceState,
): InvoiceState => {
  const { sellerConfirmationBusinessFields, sellerConfirmationPrivateFields } = invoiceDefinitions;
  const { isBusiness } = invoiceUtils;

  if (isChecked) {
    return invoice;
  }

  const fields = isBusiness(invoiceType)
    ? sellerConfirmationBusinessFields
    : sellerConfirmationPrivateFields;

  return omit(invoice, fields);
};

// clear invoice/address post code when there is contract/billing post code error
// and useDeliveryDetailsForInvoice is unchecked
// (the latter means that it WAS checked before current action was dispatched)
export const clearPostCodeFieldWithError = (
  useDeliveryDetailsForInvoice: boolean,
  formState: InvoiceState | AddressState,
  fieldName: string,
) =>
  !useDeliveryDetailsForInvoice
    ? {
      ...formState,
      [fieldName]: {
        ...formState[fieldName],
        value: '',
        valid: false,
        error: ERROR_MESSAGES.postalCode,
      },
    }
    : {};
